import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material/Select';
import { Camera, CameraResultType, CameraSource, PermissionStatus } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { UserType, RegistrationFormData } from '../types/user';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Button,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import {
  NavigateNext,
  NavigateBefore,
  ArrowBack,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

// Import components
import CommonFields from './registration/CommonFields';
import CompanyFields from './registration/CompanyFields';
import ContractorFields from './registration/ContractorFields';
import AmbassadorFields from './registration/AmbassadorFields';
import ContactFields from './registration/ContactFields';
import PhotoUpload from './registration/PhotoUpload';

interface Props {
  userType: UserType;
  onSubmit: (data: RegistrationFormData) => Promise<void>;
}

const RegistrationForm: React.FC<Props> = ({ userType, onSubmit }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<RegistrationFormData>({
    email: '',
    password: '',
    type: userType,
    profilePhoto: null,
    backgroundPhoto: null,
    emailVerified: false,
    
    // Company & Contractor
    companyName: '',
    businessType: { isOther: false, value: '', customValue: null },
    siret: '',
    industry: { isOther: false, value: '', customValue: null },
    description: '',
    
    // Address
    city: '',
    postalCode: '',
    country: '',
    phone: '',
    professionalEmail: '',
    website: '',
    
    // Representative
    legalRepName: '',
    position: '',
    contactEmail: '',
    contactPhone: '',
    
    // Contractor specific
    firstName: '',
    lastName: '',
    serviceType: { isOther: false, value: '', customValue: null },
    specialization: '',
    legalStatus: '',
    experience: '',
    servicesDescription: '',
    availability: '',
    pricing: '',
    
    // Ambassador specific
    username: '',
    activityType: { isOther: false, value: '', customValue: null },
    ambassadorSpecialization: { isOther: false, value: '', customValue: null },
    ambassadorDescription: '',
    followersCount: 0,
    socialLinks: {},
    
    // Documents
    documents: []
  });

  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string>('');
  const [backgroundPhotoPreview, setBackgroundPhotoPreview] = useState<string>('');
  const [step, setStep] = useState(1);
  const [showSubmit, setShowSubmit] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const totalSteps = 4; // Account, Info, Contact, Photos

  // Request permissions proactively on component mount
  React.useEffect(() => {
    const requestPermissions = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          // Request all permissions upfront
          await Camera.requestPermissions({
            permissions: ['camera', 'photos']
          });
        } catch (error) {
          console.warn('Failed to request permissions proactively:', error);
        }
      }
    };
    
    requestPermissions();
  }, []);

  const handleInputChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (field.includes('.')) {
      // Handle nested properties (e.g., 'activityType.customValue')
      const [parent, child] = field.split('.');
      setFormData(prev => {
        const parentField = prev[parent as keyof RegistrationFormData];
        if (parentField && typeof parentField === 'object' && !Array.isArray(parentField)) {
          return {
            ...prev,
            [parent]: {
              ...parentField,
              [child]: event.target.value
            }
          };
        }
        return prev;
      });
    } else {
      setFormData(prev => ({ ...prev, [field]: event.target.value }));
    }
  };

  const handleSelectChange = (field: string) => (
    event: SelectChangeEvent,
    isCustomField?: boolean
  ) => {
    if (isCustomField) {
      const value = event.target.value;
      const isOther = value === 'other';
      setFormData(prev => ({
        ...prev,
        [field]: {
          isOther,
          value,
          customValue: isOther ? '' : null // Initialize with empty string for 'other', null otherwise
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: event.target.value }));
    }
  };

  const handlePhotoCapture = async (type: 'profile' | 'background', source: 'camera' | 'gallery') => {
    try {
      // Check if we're on a native platform (iOS/Android)
      if (Capacitor.isNativePlatform()) {
        // Request permissions proactively
        let permissionStatus: PermissionStatus;
        
        if (source === 'gallery') {
          // Request photos permission
          permissionStatus = await Camera.requestPermissions({
            permissions: ['photos']
          });
          
          if (permissionStatus.photos !== 'granted') {
            setError(t('registration.photos.gallery_permission_denied'));
            return;
          }
        } else if (source === 'camera') {
          // Request camera permission
          permissionStatus = await Camera.requestPermissions({
            permissions: ['camera']
          });
          
          if (permissionStatus.camera !== 'granted') {
            setError(t('registration.photos.camera_permission_denied'));
            return;
          }
        }
      }
      
      // Proceed with photo capture if permissions are granted or on web
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Uri,
        source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos
      });

      const response = await fetch(image.webPath!);
      const blob = await response.blob();
      const file = new File([blob], `${type}.jpg`, { type: 'image/jpeg' });

      if (type === 'profile') {
        setFormData(prev => ({ ...prev, profilePhoto: file }));
        setProfilePhotoPreview(image.webPath!);
      } else {
        setFormData(prev => ({ ...prev, backgroundPhoto: file }));
        setBackgroundPhotoPreview(image.webPath!);
      }
      
      // Clear any previous errors
      setError('');
    } catch (err: any) {
      console.error('Error capturing photo:', err);
      
      // Check if the error is related to permissions
      if (err.message && (
          err.message.includes('permission') ||
          err.message.includes('denied') ||
          err.message.includes('access') ||
          err.message.includes('SecurityException')
      )) {
        if (source === 'camera') {
          setError(t('registration.photos.camera_permission_denied'));
        } else {
          setError(t('registration.photos.gallery_permission_denied'));
        }
      } else if (err.message && err.message.includes('User cancelled')) {
        // User cancelled the photo selection
        setError('');
      } else {
        setError(t('registration.photos.photo_capture_error'));
      }
    }
  };

  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setFormData(prev => ({
      ...prev,
      documents: [...(prev.documents || []), ...files]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    // Only handle submit if it's coming from the submit button
    if ((e.nativeEvent as SubmitEvent).submitter?.getAttribute('type') === 'submit') {
      e.preventDefault();
      setLoading(true);
      setError('');

      try {
        await onSubmit(formData);
      } catch (err: any) {
        setError(err?.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    } else {
      e.preventDefault(); // Prevent form submission for other button clicks
    }
  };

  const validateStep = (currentStep: number): boolean => {
    switch (currentStep) {
      case 1: // Account
        return !!formData.email && !!formData.password && acceptedTerms;
      case 2: // Info
        switch (userType) {
          case 'company':
            return !!formData.companyName && 
              !!formData.businessType.value && 
              (!formData.businessType.isOther || !!formData.businessType.customValue) && 
              !!formData.industry.value && 
              (!formData.industry.isOther || !!formData.industry.customValue) && 
              !!formData.description;
          case 'contractor':
            return !!formData.firstName && 
              !!formData.lastName && 
              !!formData.serviceType.value && 
              (!formData.serviceType.isOther || !!formData.serviceType.customValue) && 
              !!formData.experience && 
              !!formData.servicesDescription;
          case 'ambassador':
            return !!formData.firstName && 
              !!formData.lastName && 
              !!formData.username && 
              !!formData.activityType.value && 
              (!formData.activityType.isOther || !!formData.activityType.customValue) &&
              !!formData.ambassadorSpecialization.value &&
              (!formData.ambassadorSpecialization.isOther || !!formData.ambassadorSpecialization.customValue) &&
              !!formData.ambassadorDescription &&
              (formData.activityType.value === 'influencer' ? !!formData.followersCount : true);
          default:
            return false;
        }
      case 3: // Contact
        return !!formData.city && !!formData.postalCode && !!formData.country && !!formData.phone && !!formData.professionalEmail;
      case 4: // Photos
        return true; // Photos are optional
      default:
        return false;
    }
  };

  const getValidationErrors = (currentStep: number): string[] => {
    const errors: string[] = [];
    
    switch (currentStep) {
      case 1: // Account
        if (!formData.email) errors.push(t('registration.errors.emailRequired'));
        if (!formData.password) errors.push(t('registration.errors.passwordRequired'));
        if (!acceptedTerms) errors.push(t('registration.errors.termsRequired'));
        break;
        
      case 2: // Info
        switch (userType) {
          case 'company':
            if (!formData.companyName) errors.push(t('registration.errors.companyNameRequired'));
            if (!formData.businessType.value) errors.push(t('registration.errors.businessTypeRequired'));
            if (formData.businessType.isOther && !formData.businessType.customValue) {
              errors.push(t('registration.errors.businessTypeCustomRequired'));
            }
            if (!formData.industry.value) errors.push(t('registration.errors.industryRequired'));
            if (formData.industry.isOther && !formData.industry.customValue) {
              errors.push(t('registration.errors.industryCustomRequired'));
            }
            if (!formData.description) errors.push(t('registration.errors.descriptionRequired'));
            break;
            
          case 'contractor':
            if (!formData.firstName) errors.push(t('registration.errors.firstNameRequired'));
            if (!formData.lastName) errors.push(t('registration.errors.lastNameRequired'));
            if (!formData.serviceType.value) errors.push(t('registration.errors.serviceTypeRequired'));
            if (formData.serviceType.isOther && !formData.serviceType.customValue) {
              errors.push(t('registration.errors.serviceTypeCustomRequired'));
            }
            if (!formData.experience) errors.push(t('registration.errors.experienceRequired'));
            if (!formData.servicesDescription) errors.push(t('registration.errors.servicesDescriptionRequired'));
            break;
            
          case 'ambassador':
            if (!formData.firstName) errors.push(t('registration.errors.firstNameRequired'));
            if (!formData.lastName) errors.push(t('registration.errors.lastNameRequired'));
            if (!formData.username) errors.push(t('registration.errors.usernameRequired'));
            if (!formData.activityType.value) errors.push(t('registration.errors.activityTypeRequired'));
            if (formData.activityType.isOther && !formData.activityType.customValue) {
              errors.push(t('registration.errors.activityTypeCustomRequired'));
            }
            if (!formData.ambassadorSpecialization.value) errors.push(t('registration.errors.specializationRequired'));
            if (formData.ambassadorSpecialization.isOther && !formData.ambassadorSpecialization.customValue) {
              errors.push(t('registration.errors.specializationCustomRequired'));
            }
            if (!formData.ambassadorDescription) errors.push(t('registration.errors.ambassadorDescriptionRequired'));
            if (formData.activityType.value === 'influencer' && !formData.followersCount) {
              errors.push(t('registration.errors.followersCountRequired'));
            }
            break;
        }
        break;
        
      case 3: // Contact
        if (!formData.city) errors.push(t('registration.errors.cityRequired'));
        if (!formData.postalCode) errors.push(t('registration.errors.postalCodeRequired'));
        if (!formData.country) errors.push(t('registration.errors.countryRequired'));
        if (!formData.phone) errors.push(t('registration.errors.phoneRequired'));
        if (!formData.professionalEmail) errors.push(t('registration.errors.professionalEmailRequired'));
        break;
        
      case 4: // Photos
        // Photos are optional, no errors
        break;
    }
    
    return errors;
  };

  const nextStep = () => {
    if (step < totalSteps && validateStep(step)) {
      setStep(step + 1);
      setError('');
      // Reset showSubmit when moving to any step
      setShowSubmit(false);
      // If moving to the last step, delay showing the submit button
      if (step + 1 === totalSteps) {
        setTimeout(() => {
          setShowSubmit(true);
        }, 500);
      }
    } else {
      const errors = getValidationErrors(step);
      if (errors.length > 0) {
        // Show the first error, or combine multiple errors
        if (errors.length === 1) {
          setError(errors[0]);
        } else {
          setError(t('registration.errors.multipleFieldsRequired') + ': ' + errors.join(', '));
        }
      } else {
        setError(t('registration.fillRequiredFields'));
      }
    }
  };
  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      // Reset showSubmit when changing steps
      setShowSubmit(false);
      // If moving to the last step, delay showing the submit button
      if (step - 1 === totalSteps) {
        setTimeout(() => {
          setShowSubmit(true);
        }, 500);
      }
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1: // Account
        return (
          <CommonFields
            email={formData.email}
            password={formData.password}
            onChange={handleInputChange}
          />
        );
      case 2: { // Info
        let infoComponent;
        switch (userType) {
          case 'company':
            infoComponent = (
              <CompanyFields
                companyName={formData.companyName}
                businessType={formData.businessType}
                siret={formData.siret}
                industry={formData.industry}
                specialization={formData.specialization}
                description={formData.description}
                onInputChange={handleInputChange}
                onSelectChange={handleSelectChange}
              />
            );
            break;
          case 'contractor':
            infoComponent = (
              <ContractorFields
                firstName={formData.firstName}
                lastName={formData.lastName}
                serviceType={formData.serviceType}
                specialization={formData.specialization}
                experience={formData.experience}
                servicesDescription={formData.servicesDescription}
                onInputChange={handleInputChange}
                onSelectChange={handleSelectChange}
              />
            );
            break;
          case 'ambassador':
            infoComponent = (
              <AmbassadorFields
                firstName={formData.firstName}
                lastName={formData.lastName}
                username={formData.username}
                activityType={formData.activityType}
                specialization={formData.ambassadorSpecialization}
                ambassadorDescription={formData.ambassadorDescription}
                followersCount={formData.followersCount}
                onInputChange={handleInputChange}
                onSelectChange={handleSelectChange}
              />
            );
            break;
        }
        return infoComponent;
      }
      case 3: // Contact
        return (
          <ContactFields
            city={formData.city}
            postalCode={formData.postalCode}
            country={formData.country}
            phone={formData.phone}
            professionalEmail={formData.professionalEmail}
            website={formData.website}
            onInputChange={handleInputChange}
          />
        );
      case 4: // Photos
        return (
          <PhotoUpload
            profilePhotoPreview={profilePhotoPreview}
            backgroundPhotoPreview={backgroundPhotoPreview}
            onPhotoCapture={handlePhotoCapture}
          />
        );
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      <Box sx={{gap:2, mb: 2, display: 'flex', alignItems: 'center' }}>
        <ArrowBack onClick={() => history.push('/register')}  />
        <Typography 
          variant="h5" 
          className="animated-gradient-text" 
          gutterBottom 
          sx={{ 
            mt:1,
            fontFamily: 'Poppins, sans-serif'
          }}
        >
          {t('registration.register')}
        </Typography>
      </Box>

      <Box sx={{ width: '100%', mb: 4 }}>
        <Stepper 
          activeStep={step - 1} 
          alternativeLabel={false}
          sx={{ 
            '& .MuiStepConnector-line': {
              borderColor: '#e0e0e0',
              borderTopWidth: '1px',
              borderStyle: 'solid',
              opacity: 0.5
            },
            '& .MuiStepConnector-root': {
              top: '12px',
              left: 'calc(-50% + 12px)',
              right: 'calc(50% + 12px)'
            },
            '& .MuiStepLabel-root': {
              padding: 0
            }
          }}
        >
          {[1, 2, 3, 4].map((num, index) => (
            <Step key={num}>
              <StepLabel 
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: step - 1 >= index 
                        ? 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
                        : '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: step - 1 >= index ? 'white' : '#757575',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      transition: 'all 0.3s ease',
                      boxShadow: step - 1 >= index 
                        ? '0 3px 5px 2px rgba(33, 203, 243, .15)'
                        : 'none',
                      '&:hover': {
                        opacity: 0.9,
                        transform: 'scale(1.05)'
                      }
                    }}
                    onClick={() => {
                      // Only allow moving to next step or previous completed steps
                      if (index + 1 <= step || (index + 1 === step + 1 && validateStep(step))) {
                        setStep(index + 1);
                        setError('');
                        // Reset showSubmit when changing steps
                        setShowSubmit(false);
                        // If moving to the last step, delay showing the submit button
                        if (index + 1 === totalSteps) {
                          setTimeout(() => {
                            setShowSubmit(true);
                          }, 500);
                        }
                      }
                    }}
                  >
                    {num}
                  </Box>
                )}
              />
            </Step>
          ))}
        </Stepper>
      </Box>

      {renderStepContent()}

      {/* Terms acceptance checkbox - only show on first step */}
      {step === 1 && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t('registration.acceptTerms.text')}{' '}
                <Link
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'primary.main', textDecoration: 'underline' }}
                >
                  {t('registration.acceptTerms.link')}
                </Link>
              </Typography>
            }
          />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3, mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        {step > 1 && (
          <Button
            variant="outlined"
            onClick={prevStep}
            startIcon={<NavigateBefore />}
          >
            {t('common.back')}
          </Button>
        )}
        
        <Box sx={{ ml: 'auto' }}>
          {step < totalSteps ? (
            <Button
              variant="contained"
              onClick={nextStep}
              endIcon={<NavigateNext />}
            >
              {t('common.next')}
            </Button>
          ) : (
            showSubmit && (
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                endIcon={loading ? <CircularProgress size={20} /> : undefined}
              >
                {t('common.submit')}
              </Button>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RegistrationForm;
